import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';

import { Sparkles, ArrowRight, Trophy, Calendar } from 'lucide-react';

const TXT: Record<string, any> = {
  fr: { badge: 'À la une', title: 'Serveurs à la une', sub: 'Nos coups de cœur du moment, actualisés chaque jour.', all: 'Voir tous les articles', prestige: 'Prestige', today: "Aujourd'hui" },
  en: { badge: 'Spotlight', title: 'Featured servers', sub: 'Our daily editorial picks — fresh every day.', all: 'See all articles', prestige: 'Prestige', today: 'Today' },
  es: { badge: 'Destacados', title: 'Servidores destacados', sub: 'Nuestras selecciones diarias, renovadas cada día.', all: 'Ver todos los artículos', prestige: 'Prestige', today: 'Hoy' },
};

export const SpotlightHomeWidget: React.FC = () => {
  const { language } = useLanguage();
  const t = TXT[language] || TXT.fr;
  const [items, setItems] = useState<any[]>([]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('server_spotlights')
        .select('id, spotlight_date, slug, meta_title, meta_description, is_prestige, server_id')
        .eq('language', language)
        .order('spotlight_date', { ascending: false })
        .limit(4);
      const list = data || [];
      const ids = Array.from(new Set(list.map((s: any) => s.server_id)));
      if (ids.length) {
        const { data: srvs } = await supabase.from('servers').select('id,logo_image_url,logo_url,banner_image_url,banner_url').in('id', ids);
        const map = new Map((srvs || []).map((s: any) => [s.id, s]));
        list.forEach((it: any) => { it.server = map.get(it.server_id); });
      }
      setItems(list);
    })();
  }, [language]);

  if (!items.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  const SITE_ICON = '/logo.png';
  const pickImg = (s: any) =>
    s?.banner_image_url || s?.banner_url || s?.logo_image_url || s?.logo_url || SITE_ICON;

  return (
    <section className="container mx-auto px-4 py-4">
      {/* Header row */}
      <div className="flex items-end justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/15 border border-primary/30 text-[10px] font-semibold shrink-0">
            <Sparkles className="w-3 h-3 text-primary" /> {t.badge}
          </div>
          <div className="min-w-0">
            <h2 className="text-base md:text-lg font-bold leading-tight truncate">{t.title}</h2>
            <p className="text-[11px] text-muted-foreground truncate">{t.sub}</p>
          </div>
        </div>
        <Link
          to={`/${language}/spotlight`}
          className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary/10 hover:bg-primary/20 border border-primary/40 text-primary text-xs font-semibold transition"
        >
          {t.all} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {/* Thin horizontal strip of articles */}
      <div className="flex flex-col gap-2">
        {items.slice(0, 4).map((it) => {
          const img = pickImg(it.server);
          return (
            <Link
              key={it.id}
              to={`/${language}/spotlight/${it.spotlight_date}/${it.slug}`}
              className="group relative overflow-hidden rounded-md border border-primary/20 hover:border-primary/60 transition bg-gradient-to-r from-primary/5 to-transparent hover:from-primary/15 flex items-center h-16"
            >
              <div className="relative w-40 md:w-56 h-full shrink-0 overflow-hidden bg-muted/40">
                <img src={img} alt={it.meta_title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-500" onError={(e) => { (e.currentTarget as HTMLImageElement).src = SITE_ICON; }} />
                <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-r from-transparent to-background pointer-events-none" />
              </div>
              <div className="flex-1 min-w-0 px-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground group-hover:text-primary transition line-clamp-1 leading-snug">
                    {it.meta_title}
                  </h3>
                  <p className="text-[10px] text-muted-foreground line-clamp-1">{it.meta_description}</p>
                </div>
                <div className="hidden md:flex items-center gap-1.5 shrink-0">
                  {it.is_prestige && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/90 text-black text-[9px] font-semibold">
                      <Trophy className="w-2.5 h-2.5" />{t.prestige}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[9px] font-medium">
                    <Calendar className="w-2.5 h-2.5" />
                    {it.spotlight_date === today ? t.today : new Date(it.spotlight_date).toLocaleDateString(language)}
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 text-primary opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};
